import Button from '@/components/Button.jsx';
import Input from '@/components/Input.jsx';
import Panel from '@/components/panel/Panel.jsx';
import { FiBarChart, HiOutlineSortAscending, HiOutlineSortDescending, SlRefresh } from '@/components/react-icons/main';
import Select from '@/components/Select.jsx';
import ToggleButton from '@/components/ToggleButton.jsx';
import DividerV2 from '@/components/v2/DividerV2.jsx';
import IconV2 from '@/components/v2/IconV2.jsx';
import { AddIcon } from '@/lib/constants/icons.js';
import getLookupMethod from '@/lib/getLookupMethod.js';
import getOrdinalName from '@/lib/getOrdinalName.js';
import normalizeToArray from '@/lib/normalizeToArray.js';
import { action as reduxAction } from '@/lib/api';
import { getFieldsFromSchemaPath, introspectionQuery } from '@/lib/graphql.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import selectors from '@/redux/selectors/selectors.js';
import argumentDataSources from '@/schema/argumentDataSources.js';
import appActions from '@/screens/apps/redux/actions/appActions.js';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import { camelToTitle } from '@shared/utils/camelToTitle.js';
import { getDeepByPath } from '@shared/utils/getDeepByPath.js';
import { randomId } from '@shared/utils/randomId.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ArgumentLabel from '../ArgumentLabel.jsx';
import getFieldOptions from '../argumentsOptionsWrapper/getFieldOptions.js';
import ArgumentWrapper from '../ArgumentWrapper.jsx';
import SliderClose from './SliderClose.jsx';
import SliderHeader from './SliderHeader.jsx';
import SliderTitle from './SliderTitle.jsx';
import useAsyncArgumentValue from '@/hooks/useAsyncArgumentValue.js';
import normalizeCustomSortFields from '@/lib/normalizeCustomSortFields.js';
import useRunStepAction from '@/hooks/useRunStepAction.js';

const SortSlider = ({ onClose, dataSource, dataSourcePath, dataSavePath, isTransformAction, isTransformApp, isPostProcessing, disabled = false }) => {
  const appId = useArgSelector(selectors.param, 'appId');
  const apps = useSelector(selectors.appsVersions);
  const stepId = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.id);
  const isNetsuite = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.appSlug === 'netsuite');
  const steps = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.steps);
  const appSlug = useSelector((state) => argumentSelectors[dataSource].activeApp(state)?.slug);
  const action = useSelector(argumentSelectors[dataSource].activeAction());
  const api = useSelector(argumentSelectors[dataSource].activeAppApi);
  const options = useSelector(argumentSelectors[dataSource].activeOptions);
  const args = useSelector(argumentSelectors[dataSource].activeArguments());
  const stepOutputs = useSelector(selectors.cookedWorkflowStepOutputs) || [];
  const sort = action?.features?.sort;
  const useSort = sort?.isActive && !isTransformAction;
  const isGraphql = api?.isGraphql === true || action?.isGraphql === true;
  const useIntrospectionFields = isGraphql && (api?.graphqlSettings?.useIntrospectionFields === true || sort?.useIntrospectionFields === true);
  const credentialId = useSelector(argumentSelectors[dataSource].activeCredentialId);
  const graphqlRoot = action?.graphqlRoot;
  const queryAction = api?.graphqlSettings?.queryAction || action?.queryAction;
  const graphqlSchema = useSelector((state) => state?.workflow?.graphqlSchema?.[`${credentialId}-${queryAction}`]);
  const runStepAction = useRunStepAction(dataSource);
  const [isGraphqlLoading, setIsGraphqlLoading] = useState(false);

  // recordType
  const recordTypeArgDef = action?.arguments?.find((n) => n.key === 'recordType');
  const recordType = useAsyncArgumentValue({ arg: args?.recordType, def: recordTypeArgDef, responses: stepOutputs });
  // isCustomRecord
  const isCustomRecordArgDef = action?.arguments?.find((n) => n.key === 'isCustomRecord');
  const isCustomRecord = useAsyncArgumentValue({ arg: args?.isCustomRecord, def: isCustomRecordArgDef, responses: stepOutputs });

  const lookupSettings = useMemo(() => {
    if (appSlug === 'netsuite' && isCustomRecord) {
      return {
        ...sort,
        actionInstance: {
          arguments: {
            recordType: { value: recordType },
            tableName: { value: recordType },
            requiresRecordType: { value: false }
          }
        }
      };
    }

    return sort;
  }, [sort, appSlug, isCustomRecord, recordType]);

  const sortOptions = useMemo(() => {
    if (isTransformAction || isTransformApp) {
      return options?.postProcessing?.sortCriteria || options?.sortCriteria || [];
    }

    if (useSort) {
      return options?.sort || [];
    }

    return options?.sortCriteria || [];
  }, [isTransformAction, isTransformApp, useSort, options]);

  const normalizedOptions = normalizeToArray(sortOptions);
  const computedDataSavePath = dataSavePath ? `${dataSavePath}.options${isPostProcessing ? '.postProcessing' : ''}` : dataSourcePath;
  const computedKey = useSort ? 'sort' : 'sortCriteria';

  const lookupMethod = useMemo(() => getLookupMethod(dataSource), [dataSource]);
  const { lookup, loading, reload } = lookupMethod({ id: 'sort', lookupSettings });
  const graphqlLeafFieldOptions = useMemo(() => {
    if (!useIntrospectionFields || !graphqlSchema?.schema || !graphqlRoot) {
      return [];
    }

    const rawFields = getFieldsFromSchemaPath(graphqlSchema.schema, graphqlRoot);
    if (!rawFields) {
      return [];
    }

    return Object.entries(rawFields)
      .filter(([, definition]) => !definition?.fields)
      .map(([fieldName]) => ({
        label: camelToTitle(fieldName),
        value: fieldName
      }));
  }, [useIntrospectionFields, graphqlSchema, graphqlRoot]);

  const fetchGraphqlSchema = useCallback(async () => {
    if (!useIntrospectionFields || !queryAction || !credentialId) {
      return;
    }

    setIsGraphqlLoading(true);

    try {
      const response = await runStepAction(queryAction, {
        arguments: {
          query: { value: introspectionQuery }
        }
      });

      const simpleSchema = response?.[0];
      const schema = getDeepByPath('Schema', simpleSchema);

      if (schema && schema.subscriptionType && Object.keys(schema.subscriptionType).length === 0) {
        delete schema.subscriptionType;
      }

      if (schema) {
        reduxAction('SET_GRAPHQL_SCHEMA', { credentialId, queryAction, schema });
      }
    } finally {
      setIsGraphqlLoading(false);
    }
  }, [useIntrospectionFields, queryAction, credentialId, runStepAction]);

  useEffect(() => {
    if (useIntrospectionFields && !graphqlSchema && credentialId && queryAction) {
      fetchGraphqlSchema();
    }
  }, [useIntrospectionFields, graphqlSchema, credentialId, queryAction, fetchGraphqlSchema]);

  const fieldActionIdMap = {
    [argumentDataSources.appLongPollingActionInstance]: action?.longPolling?.actionId,
    [argumentDataSources.appLongPollingExtraActionInstance]: action?.longPolling?.extraAction?.id,
    [argumentDataSources.appActionOnCompletionHookActionInstance]: action?.hooks?.onCompletion?.actionId
  };

  const actions = dataSource === argumentDataSources.workflowStep ? stepActions : appActions;
  const fieldOptions = useMemo(() => {
    if (isTransformAction || isTransformApp) {
      return getFieldOptions({ actionId: fieldActionIdMap[dataSource], stepId, apps, steps, appId });
    }

    if (useSort) {
      if (useIntrospectionFields) {
        return graphqlLeafFieldOptions;
      }

      if (sort?.useDynamicFields) {
        return null;
      }

      if (sort?.useLookupFields) {
        return lookup;
      }

      if (sort?.useCustomFields) {
        return normalizeCustomSortFields(sort?.customFields);
      }

      return (action?.attributes || [])?.map((n) => ({ label: camelToTitle(n.key), value: n.key }));
    }

    return null;
  }, [isTransformAction, isTransformApp, useSort, useIntrospectionFields, graphqlLeafFieldOptions, sort, dataSource, fieldActionIdMap, stepId, apps, steps, appId, action, lookup]);

  const formattedFieldOptions = useMemo(() => {
    if (!isNetsuite) {
      return fieldOptions;
    }
    return fieldOptions?.map((option) => ({ ...option, tag: option.value }));
  }, [fieldOptions, isNetsuite]);

  const valueOptions = [
    { value: 'asc', label: 'Ascending', icon: <HiOutlineSortAscending />, iconProps: { className: 'light-medium' } },
    { value: 'desc', label: 'Descending', icon: <HiOutlineSortDescending />, iconProps: { className: 'light-medium' } }
  ];

  const handleChange = ({ id, key, value }) => {
    if (!id || !key || !value) {
      return;
    }
    actions.set(`${computedDataSavePath}.${computedKey}`, (m) => {
      return m?.map((s) => (s?.id === id ? { ...s, [key]: value } : s));
    });
  };

  const handleFieldChange = ({ id, value, providerToken }) => {
    if (!id || !value) {
      return;
    }
    actions.set(`${computedDataSavePath}.${computedKey}`, (currentSorts) => {
      return currentSorts?.map((sortItem) => {
        if (sortItem?.id !== id) {
          return sortItem;
        }

        if (providerToken) {
          return { ...sortItem, field: value, providerToken };
        }

        const nextSortItem = { ...sortItem, field: value };
        delete nextSortItem.providerToken;
        return nextSortItem;
      });
    });
  };

  const handleDelete = (option) => {
    actions.set(`${computedDataSavePath}.${computedKey}`, (n = []) => {
      return n?.filter((m) => m?.id !== option?.id);
    });
  };

  const handleAddSort = () => {
    const sortId = randomId();
    actions.set(`${computedDataSavePath}.${computedKey}`, (n = []) => [...normalizeToArray(n), { id: sortId, value: 'asc' }]);
  };

  const handleDeleteAll = () => {
    actions.set(`${computedDataSavePath}.${computedKey}`, []);
    onClose();
  };

  return (
    <>
      <SliderHeader onClose={onClose} showDelete showRun onDelete={handleDeleteAll} disabled={disabled} />
      <SliderTitle //
        icon={FiBarChart}
        title="Sort Your Results"
        description="Change the order of the action results"
        helpIconContext={isTransformAction || isTransformApp ? 'transformPanelSortContext' : 'workflowStepPostSortContext'}
      />
      {normalizedOptions?.map((option, idx) => (
        <Panel.Section key={idx} title={`${getOrdinalName(idx + 1)} Sort`} onDelete={() => handleDelete(option)}>
          <ArgumentWrapper>
            <ArgumentLabel className={'small-gap'} label={'Field'} description={'Select the field to sort your data'} />
            <div className="workflow-select-wrapper">
              {sort?.useDynamicFields ? (
                <Input disabled={disabled} placeholder={'Enter field name'} value={option?.field || ''} onChange={(e) => handleChange({ id: option?.id, key: 'field', value: e?.target?.value })} size="sm" />
              ) : (
                <>
                  <Select //
                    disabled={disabled}
                    className={'filter-field'}
                    options={formattedFieldOptions}
                    value={formattedFieldOptions?.find((n) => n?.value === option?.field)?.label || option?.field}
                    onChange={({ value, providerToken }) => handleFieldChange({ id: option?.id, value, providerToken })}
                    showIcon={false}
                  />
                  {(sort?.useLookupFields || useIntrospectionFields) && (
                    <div className="workflow-input-actions">
                      <IconV2 icon={SlRefresh} className={`reload-icon ${(useIntrospectionFields ? isGraphqlLoading : loading) ? 'loading' : ''}`} onMouseUp={useIntrospectionFields ? fetchGraphqlSchema : reload} title={useIntrospectionFields ? 'Reload Schema' : 'Reload Data'} />
                    </div>
                  )}
                </>
              )}
            </div>
          </ArgumentWrapper>
          <ArgumentWrapper>
            <ArgumentLabel className={'small-gap'} label={'Direction'} description={'Select the direction to sort your data'} />
            <ToggleButton //
              options={valueOptions}
              value={option?.value}
              onChange={(value) => handleChange({ id: option?.id, key: 'value', value })}
              disabled={disabled}
            />
          </ArgumentWrapper>
        </Panel.Section>
      ))}
      <DividerV2 />
      <Button className="medium full-width" icon={AddIcon} onClick={handleAddSort} disabled={disabled}>
        Add Sort
      </Button>
      <SliderClose onClose={onClose} />
    </>
  );
};

export default SortSlider;
